import { useState, useEffect } from 'react';
import { useMotionValue, useAnimate } from 'framer-motion';
import { SmallLoader } from './SmallLoader';

type ButtonTinyVariant = "close" | "back" | "menu" | "delete" | "custom";
export type ButtonColorVariant = "white" | "black" | "gray";

const ICON_FILL_COLOR = "#18010C";

interface ButtonTinyProps {
    onClick: () => void;
    variant: ButtonTinyVariant;
    colorVariant?: ButtonColorVariant;
    bordered?: boolean;
    rounded?: boolean;
    secondsToActivate?: number;
    label?: string;
    icon?: React.ReactNode;
    className?: string;
}

export const ButtonTiny = ({ onClick, variant, colorVariant = "gray", bordered = false, rounded = false, secondsToActivate, label, icon, className }: ButtonTinyProps) => {
    const [isActive, setIsActive] = useState(secondsToActivate ? false : true);
    const progressValue = useMotionValue(0);
    const [scope, animate] = useAnimate();
    const [currentProgress, setCurrentProgress] = useState(0);

    const getColorVariant = () => {
        if (colorVariant === "gray") {
            return "bg-osGrey-5 text-osBlack";
        }
        if (colorVariant === "white") {
            return "bg-white text-osBlack";
        }
        if (colorVariant === "black") {
            return "bg-black text-white";
        }
    }

    useEffect(() => {
        if (secondsToActivate && secondsToActivate > 0) {
            // Animate progress from 0 to 100 over the specified duration
            animate(progressValue, 100, {
                duration: secondsToActivate,
                ease: "linear",
                onUpdate: (latest) => {
                    setCurrentProgress(latest);
                },
                onComplete: () => {
                    setIsActive(true);
                }
            });
        }
    }, [secondsToActivate, animate, progressValue]);

    const handleClick = () => {
        if (!isActive) {
            return;
        }

        onClick();
    };

    const renderIcon = () => {
        if (variant === "custom") {
            return (
                icon
            );
        }

        if (variant === "back") {
            return (
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.3518 11.7634L16.6608 7.50154C17.8111 6.02002 15.7863 4.19621 14.3065 5.38094L6.77706 11.4092C5.74098 12.2387 5.74098 13.7613 6.77706 14.5908L14.3065 20.6191C15.7863 21.8038 17.8111 19.98 16.6608 18.4985L13.3518 14.2366C12.7816 13.5022 12.7816 12.4978 13.3518 11.7634Z" fill={ICON_FILL_COLOR} />
                </svg>
            );
        }

        if (variant === "close") {
            return (
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.32695 8.59017C5.26181 7.09906 7.09895 5.26193 8.59006 6.32708L12.0569 8.80355C12.621 9.2065 13.3788 9.2065 13.9429 8.80355L17.4098 6.32707C18.9009 5.26193 20.738 7.09907 19.6729 8.59018L17.1964 12.057C16.7934 12.6211 16.7934 13.3789 17.1964 13.943L19.6729 17.4099C20.738 18.901 18.9009 20.7381 17.4098 19.673L13.9429 17.1965C13.3788 16.7935 12.621 16.7935 12.0569 17.1965L8.59005 19.673C7.09894 20.7381 5.26181 18.901 6.32696 17.4099L8.80343 13.943C9.20638 13.3789 9.20638 12.6211 8.80343 12.057L6.32695 8.59017Z" fill={ICON_FILL_COLOR} />
                </svg>
            );
        }

        if (variant === "menu") {
            return (
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 7C20.1046 7 21 7.89543 21 9C21 10.1046 20.1046 11 19 11H7C5.89543 11 5 10.1046 5 9C5 7.89543 5.89543 7 7 7H19Z" fill={ICON_FILL_COLOR} />
                    <path d="M13 15C14.1046 15 15 15.8954 15 17C15 18.1046 14.1046 19 13 19H7C5.89543 19 5 18.1046 5 17C5 15.8954 5.89543 15 7 15H13Z" fill={ICON_FILL_COLOR} />
                </svg>
            );
        }

        if (variant === "delete") {
            return (
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.5 5C8.5 4.17157 9.17157 3.5 10 3.5H16C16.8284 3.5 17.5 4.17157 17.5 5V6.5H20C20.5523 6.5 21 6.94772 21 7.5C21 8.05228 20.5523 8.5 20 8.5H19.5V19C19.5 20.3807 18.3807 21.5 17 21.5H9C7.61929 21.5 6.5 20.3807 6.5 19V8.5H6C5.44772 8.5 5 8.05228 5 7.5C5 6.94772 5.44772 6.5 6 6.5H8.5V5ZM10.5 5V6.5H15.5V5H10.5ZM8.5 8.5V19C8.5 19.2761 8.72386 19.5 9 19.5H17C17.2761 19.5 17.5 19.2761 17.5 19V8.5H8.5ZM11 11C11.5523 11 12 11.4477 12 12V16C12 16.5523 11.5523 17 11 17C10.4477 17 10 16.5523 10 16V12C10 11.4477 10.4477 11 11 11ZM15 11C15.5523 11 16 11.4477 16 12V16C16 16.5523 15.5523 17 15 17C14.4477 17 14 16.5523 14 16V12C14 11.4477 14.4477 11 15 11Z" fill={ICON_FILL_COLOR} />
                </svg>
            );
        }

        return null;
    };

    const getButtonStyles = () => {
        const baseStyles = `flex items-center justify-center ${getColorVariant()} ${label ? 'px-2 gap-1' : 'w-8 '} h-8 ${bordered ? 'border-2 border-white' : ''} ${rounded ? 'rounded-full' : 'rounded-[12px]'}`;
        return baseStyles;
    };

    const showLoader = secondsToActivate && !isActive;

    return (
        <button onClick={handleClick} className={getButtonStyles() + ' ' + className} ref={scope}>
            {label && <span className={`text-sm block ${showLoader ? 'text-osGrey-2' : 'text-osBlack'}`}>{label}</span>}
            {showLoader ? (
                <span className="w-[26px] flex items-center justify-center">
                    <SmallLoader progress={currentProgress} />
                </span>
            ) : (
                renderIcon()
            )}
        </button>
    );
}; 